import type { SpotlightTarget } from '~/composables/unified/useSpotlight';
import type { FragmentType } from '~/graphql/generated';
import type { SpotlightViewModel } from '~/types/unified';

import { spotlightFragment } from '~/graphql/documents/content';
import { publicFileFragment } from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated';
import { SpotlightTargetType } from '~/graphql/generated/graphql';

export const mapSpotlight = (
  fragment?: FragmentType<typeof spotlightFragment> | null,
): SpotlightViewModel | null => {
  const raw = useFragment(spotlightFragment, fragment);

  if (!raw) {
    return null;
  }

  const image = useFragment(publicFileFragment, raw.image);

  return {
    enabled: raw.enabled,
    heading: raw.heading ?? null,
    imageUrl: image?.publicUrl ?? null,
    imageAlt: raw.imageAlt ?? null,
    links: raw.links.map(link => ({
      title: link.title,
      url: link.url,
      description: link.description ?? null,
    })),
  };
};

/** What a placement knows of the collection or asset its spotlight hangs off. */
interface SpotlightTargetFields {
  id: string;
  slug?: string | null;
  name?: string | null;
  partnerId?: string | null;
  partnerName?: string | null;
}

const toSpotlightTarget = (type: SpotlightTargetType, fields: SpotlightTargetFields): SpotlightTarget => ({
  type,
  id: fields.id,
  slug: fields.slug || null,
  name: fields.name || null,
  partnerId: fields.partnerId ?? null,
  partnerName: fields.partnerName ?? null,
});

/** A learning asset as its spotlight's click names it. The partner is the asset's own. */
export const assetSpotlightTarget = (asset: SpotlightTargetFields) =>
  toSpotlightTarget(SpotlightTargetType.LearningAsset, asset);

/** A collection as its spotlight's click names it. The partner is the issuer of its badge. */
export const collectionSpotlightTarget = (collection: SpotlightTargetFields) =>
  toSpotlightTarget(SpotlightTargetType.Collection, collection);
